import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/api/reports/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Sales',
      value: formatCurrency(dashboardData?.summary?.totalSales),
      icon: CurrencyDollarIcon
    },
    {
      name: 'Total Expenses',
      value: formatCurrency(dashboardData?.summary?.totalExpenses),
      icon: ChartBarIcon
    },
    {
      name: 'Outstanding Debt',
      value: formatCurrency(dashboardData?.summary?.totalDebt),
      icon: ExclamationTriangleIcon
    },
    {
      name: 'Stock Value',
      value: formatCurrency(dashboardData?.summary?.totalStockValue),
      icon: CubeIcon
    }
  ];

  const statTrends = {
    'Total Sales': dashboardData?.summary?.trends?.totalSales,
    'Total Expenses': dashboardData?.summary?.trends?.totalExpenses,
    'Outstanding Debt': dashboardData?.summary?.trends?.totalDebt,
    'Stock Value': dashboardData?.summary?.trends?.totalStockValue
  };

  const getTrendLabel = (trend) => {
    if (!trend) return '—';
    if (trend.isNew) return 'New';
    if (trend.changePct === null || trend.changePct === undefined) return '—';
    const sign = trend.changePct >= 0 ? '+' : '-';
    return `${sign}${Math.abs(trend.changePct).toFixed(1)}%`;
  };

  const getTrendClass = (trend) => {
    if (!trend || trend.changeType === 'neutral' || trend.changePct === 0) return 'text-secondary-500';
    return trend.changeType === 'positive' ? 'text-success-600' : 'text-red-600';
  };

  const getRoleBasedContent = () => {
    switch (user.role) {
      case 'admin':
        return <AdminDashboardContent data={dashboardData} />;
      case 'finance':
        return <FinanceDashboardContent data={dashboardData} />;
      case 'agent':
      case 'depot_sales':
        return <AgentDashboardContent data={dashboardData} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <h1 className="text-2xl font-bold text-secondary-900">
          Welcome back, {user.name}!
        </h1>
        <p className="mt-1 text-sm text-secondary-600">
          Here's what's happening with your milk distribution business today.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const trend = statTrends[stat.name];
          const trendLabel = getTrendLabel(trend);
          const showArrow = trend && !trend.isNew && trend.changePct !== null && trend.changePct !== undefined && trend.changePct !== 0;
          return (
          <div key={stat.name} className="card bg-secondary-50">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                    <stat.icon className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-600 truncate">
                      {stat.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-secondary-900">
                        {stat.value}
                      </div>
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${getTrendClass(trend)}`}>
                        {showArrow ? (
                          trend.changeType === 'positive' ? (
                            <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                          ) : (
                            <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                          )
                        ) : null}
                        <span className="sr-only">Change from previous month</span>
                        <span className={showArrow ? 'ml-1' : ''}>{trendLabel}</span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Role-specific content */}
      {getRoleBasedContent()}
    </div>
  );
};

// Admin Dashboard Content
const AdminDashboardContent = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div className="card bg-secondary-50">
      <div className="card-header">
        <h3 className="text-lg font-medium text-secondary-900">Expiring Stock</h3>
      </div>
      <div className="card-body">
        {data?.expiringStock?.length > 0 ? (
          <div className="space-y-3">
            {data.expiringStock.slice(0, 3).map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-900">
                    {item.product?.name}
                  </p>
                  <p className="text-xs text-secondary-500">
                    {item.quantity} units
                  </p>
                </div>
                <div className="text-sm text-red-600">
                  {item.daysUntilExpiry} days left
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-secondary-500">No expiring stock</p>
        )}
      </div>
    </div>
  </div>
);

// Finance Dashboard Content
const FinanceDashboardContent = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div className="card bg-secondary-50">
      <div className="card-header">
        <h3 className="text-lg font-medium text-secondary-900">Financial Summary</h3>
      </div>
      <div className="card-body">
        <div className="space-y-4">
          <div className="flex justify-between">
            <span className="text-sm text-secondary-600">Net Profit</span>
            <span className="text-lg font-semibold text-success-600">
              {formatCurrency(data?.summary?.netProfit)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-secondary-600">Profit Margin</span>
            <span className="text-sm text-secondary-900">
              {data?.summary?.totalSales > 0 
                ? ((data.summary.netProfit / data.summary.totalSales) * 100).toFixed(1)
                : 0}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="card bg-secondary-50">
      <div className="card-header">
        <h3 className="text-lg font-medium text-secondary-900">Pending Approvals</h3>
      </div>
      <div className="card-body">
        <p className="text-sm text-secondary-500">No pending expense approvals</p>
      </div>
    </div>
  </div>
);

// Agent Dashboard Content
const AgentDashboardContent = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div className="card bg-secondary-50">
      <div className="card-header">
        <h3 className="text-lg font-medium text-secondary-900">My Stock</h3>
      </div>
      <div className="card-body">
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-secondary-600">Total Units</span>
            <span className="text-sm font-medium text-secondary-900">
              {data?.stock?.totalQuantity || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-secondary-600">Total Value</span>
            <span className="text-sm font-medium text-secondary-900">
              {formatCurrency(data?.stock?.totalValue)}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="card bg-secondary-50">
      <div className="card-header">
        <h3 className="text-lg font-medium text-secondary-900">Today's Sales</h3>
      </div>
      <div className="card-body">
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-secondary-600">Sales Count</span>
            <span className="text-sm font-medium text-secondary-900">
              {data?.sales?.count || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-secondary-600">Total Amount</span>
            <span className="text-sm font-medium text-secondary-900">
              {formatCurrency(data?.sales?.totalSales)}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Dashboard;
